import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import type { TGuaranteesContractOption } from '../../../rootSection/types.ts';
import { useGuaranteesRootApplicationSectionContext } from '../RootApplicationSectionContext.tsx';
import FlowSection from '../shared/FlowSection.tsx';
import MainDealTable from './MainDealTable.tsx';

interface IMainDealSectionProps {
  contractOptions: TGuaranteesContractOption[];
  isLoadingMainDeals: boolean;
  isSavedGuaranteeTypeSelected: boolean;
  isSelectingMainDeal: boolean;
  canSaveContract: boolean;
  hasChanges?: boolean;
  lockedUntilSavedHint: string;
  selectedContract: TGuaranteesContractOption | null;
  onContractSelect: (option: TGuaranteesContractOption) => void;
  onSave: () => Promise<void>;
}

const MainDealSection: React.FC<IMainDealSectionProps> = ({
  contractOptions,
  isLoadingMainDeals,
  isSavedGuaranteeTypeSelected,
  isSelectingMainDeal,
  canSaveContract,
  hasChanges = false,
  lockedUntilSavedHint,
  selectedContract,
  onContractSelect,
  onSave,
}) => {
  const { isReadOnly, requireChangesToEnableActions } =
    useGuaranteesRootApplicationSectionContext();
  const isMainDealTableDisabled =
    !isSavedGuaranteeTypeSelected || isLoadingMainDeals || isSelectingMainDeal || isReadOnly;
  const isSaveContractDisabled =
    !canSaveContract ||
    isSelectingMainDeal ||
    isReadOnly ||
    (requireChangesToEnableActions && !hasChanges);
  const saveContractStartIcon = isSelectingMainDeal ? <ButtonLoadingIcon /> : undefined;
  const handleSaveContractClick = () => void onSave();

  return (
    <FlowSection
      step={guaranteesClientText.contractSectionStepLabel}
      title={guaranteesClientText.contractSectionTitle}
      description={guaranteesClientText.contractSectionDescription}
      disabled={!isSavedGuaranteeTypeSelected}
      required
    >
      <MainDealTable
        contractOptions={contractOptions}
        isDisabled={isMainDealTableDisabled}
        isLoading={isLoadingMainDeals}
        selectedContract={selectedContract}
        onContractSelect={onContractSelect}
      />

      {!isSavedGuaranteeTypeSelected && (
        <Typography sx={guaranteesClientPageStyles.flowLockHint}>
          {lockedUntilSavedHint}
        </Typography>
      )}

      <Box sx={guaranteesClientPageStyles.flowActions}>
        <Button
          type="button"
          variant="contained"
          disabled={isSaveContractDisabled}
          onClick={handleSaveContractClick}
          startIcon={saveContractStartIcon}
          sx={dataFormStyles.documentPrimaryAction}
        >
          {guaranteesClientText.saveContractButton}
        </Button>
      </Box>
    </FlowSection>
  );
};

export default MainDealSection;
